import { Component, OnInit, Input, EventEmitter, Output, OnChanges, forwardRef, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import { PluralPipe } from '../../pipes';
import { uuid } from '../../functions';
import { AppConstants } from '../../config';

import { IMultiSelectConfig } from './multi-select.model';
export * from './multi-select.model';

@Component({
	selector: 'multi-select',
	templateUrl: './multi-select.component.html',
	imports: [FormsModule, TranslateModule, NgbDropdownModule, NgbTooltipModule, PluralPipe],
	providers: [{
		provide: NG_VALUE_ACCESSOR,
		useExisting: forwardRef(() => MultiSelectComponent),
		multi: true
	}]
})
export class MultiSelectComponent implements OnInit, OnChanges, ControlValueAccessor {
	@Input() items: any[] = [];
	@Input() config: Partial<IMultiSelectConfig> = {};
	@Input() isDisabled = false;
	@Input() selection: any[] = [];

	@Output() selectionChange = new EventEmitter<any[]>();

	private defaultConfig: IMultiSelectConfig = {
		canSelectAll: true,
		canClearSelection: true,
		canSelectOnly: false,
		translationKey: 'MultiSelect',
		translateLabels: false,
		allSelectValue: 0,
		labelProp: 'name',
		valueProp: 'id',
		showSelectedLabels: true,
		minSelect: null, 		// TODO
		maxSelect: null,		// TODO
	};

	// For form control unique Ids
	componentId = uuid();

	get hasSelection(): boolean {
		return !!this.selection.length;
	}

	get canSelectMore(): boolean {
		return this.selection?.length < (this.config.maxSelect ?? Infinity);
	}

	get isSelectAllChecked(): boolean {
		return this.hasSelection && this.config.canSelectAll && this.selection.includes(this.config.allSelectValue);
	}

	get selectedLabels(): string {
		return (this.items || [])
			.filter(i => this.selection.includes(i[this.config.valueProp]))
			.map(i => this.getLabel(i[this.config.labelProp]))
			.join(AppConstants.LIST_ENUM_SEP_CHAR);
	}

	get selectionCount(): number {
		return this.selection.filter(v => v !== this.config.allSelectValue).length;
	}

	constructor(private translateService: TranslateService) { }

	writeValue(value: any[]): void {
		this.selection = value || [];
		this.setSelection();
	}

	registerOnChange(fn: any): void {
		this.selectionChange.subscribe(fn);
	}

	registerOnTouched(): void {
		//
	}

	setDisabledState?(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.items) {
			this.ngOnInit();
			this.setSelection();
		}

		if (changes.selection) {
			this.setSelection();
		}
	}

	ngOnInit() {
		this.config = {
			...this.defaultConfig,
			...this.config,
		};

		// Some config requirements
		if (this.config.minSelect !== null
			&& this.config.maxSelect !== null
			&& this.config.minSelect > this.config.maxSelect) {
			throw new Error('<multi-select> Config error: minSelect must be greater than maxSelect');
		}

		if (!this.config.labelProp || !this.config.valueProp) {
			throw new Error('<multi-select> Config error: labelProp and valueProp are required');
		}

		if (this.config.canSelectAll && this.config.allSelectValue === null) {
			throw new Error('<multi-select> Config error: allSelectValue is required');
		}

		if (this.config.canSelectAll &&
			this.config.allSelectValue !== null &&
			this.items.findIndex(i => i[this.config.valueProp] === this.config.allSelectValue) > -1) {
			throw new Error('<multi-select> Config error: allSelectValue should be different than other values');
		}
	}

	getLabel(label: string): string {
		return this.config.translateLabels ? this.translateService.instant(label) : label;
	}

	setSelection() {
		if (this.isSelectAllChecked) {
			this.selectAll();
		}
	}

	selectAll() {
		this.selection = this.config.canSelectAll ? [this.config.allSelectValue] : [];

		this.items.forEach(item => {
			this.selection.push(item[this.config.valueProp]);
		});
	}

	selectOnly(e: Event, item: any) {
		e.preventDefault();
		e.stopPropagation();
		this.selection = [item[this.config.valueProp]];
	}

	isItemSelected(item: any) {
		return this.selection.includes(item[this.config.valueProp]);
	}

	toggleAll(isChecked: boolean) {
		this.selection = [];

		if (isChecked) {
			this.selection.push(this.config.allSelectValue);
			this.selectAll();
		}

		this.onChange();
	}

	toggleItem(isChecked: boolean, item: any) {
		const value = item[this.config.valueProp];

		if (isChecked) {
			this.selection.push(value);
		} else {
			this.removeItem(value);
			this.removeItem(this.config.allSelectValue);
		}

		if (this.areAllItemSelected()) {
			this.selectAll();
		}

		this.onChange();
	}

	areAllItemSelected() {
		return this.selection.length >= this.items.length;
	}

	removeItem(value: any) {
		const index = this.selection.findIndex(v => v === value);
		if (index > -1) {
			this.selection.splice(index, 1);
			this.onChange();
		}
	}

	onClear() {
		this.selection = [];
		this.onChange();
	}

	onOpenChange(isOpened: boolean) {
		if (!isOpened) {
			this.onChange();
		}
	}

	onChange() {
		this.selectionChange.emit(this.selection);
	}

}
